import { useCallback } from "react";
import { sort as sortFn } from "../../../store/util";

export type SortState = {
  key: string;
  dir: "asc" | "desc" | null;
};

export type SortAction = (sort: SortState) => void;

type UseTableSortProps = {
  sort: SortState;
  setSort: SortAction;
  defaultSortKey: string;
  defaultSortDir?: "asc" | "desc";
};

type UseTableSortReturn = {
  sort: SortState;
  handleColumnClick: (columnKey: string, isSortable?: boolean) => void;
  getSortIcon: (columnKey: string) => string;
  applySorting: <T extends Record<string, any>>(data: T[]) => T[];
};

/**
 * Hook for managing table sorting with asc -> desc -> none rotation
 */
export const useTableSort = ({
  sort,
  setSort,
  defaultSortKey,
  defaultSortDir = "desc",
}: UseTableSortProps): UseTableSortReturn => {
  const handleColumnClick = useCallback(
    (columnKey: string, isSortable = true) => {
      if (!isSortable) return;

      const isSorted = sort.key === columnKey;

      if (isSorted) {
        if (sort.dir === "asc") {
          // asc -> desc
          setSort({
            key: columnKey,
            dir: "desc",
          });
        } else {
          // desc -> none (reset to default)
          setSort({
            key: defaultSortKey,
            dir: defaultSortDir,
          });
        }
      } else {
        // none -> asc
        setSort({
          key: columnKey,
          dir: "asc",
        });
      }
    },
    [sort, setSort, defaultSortKey, defaultSortDir],
  );

  const getSortIcon = useCallback(
    (columnKey: string): string => {
      if (sort.key !== columnKey) {
        return "Sort";
      }
      return sort.dir === "asc" ? "SortUp" : "SortDown";
    },
    [sort],
  );

  const applySorting = useCallback(
    <T extends Record<string, any>>(data: T[]): T[] => {
      if (!sort.key || !sort.dir) {
        return data;
      }
      return [...data].sort(sortFn(sort));
    },
    [sort],
  );

  return {
    sort,
    handleColumnClick,
    getSortIcon,
    applySorting,
  };
};

export default useTableSort;
